import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  InputGroup,
  Table,
  Button,
  DOMHelper,
  Stack,
  DateRangePicker,
  Pagination
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import ExportIcon from '@rsuite/icons/Export';
import { MdFilterList } from 'react-icons/md';
import DrawerView from './DrawerView';
import { useDispatch, useSelector } from 'react-redux';
import { fetchHistoryBalances } from '@/services/balance.service';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const DataTable = () => {
  const dispatch = useDispatch();
  const { data, loading } = useSelector(state => state.balance);
  const [showDrawer, setShowDrawer] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
    return [start, end];
  });
  const [limit, setLimit] = useState(50);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    userId: '',
    operationOwnerId: '',
    type: '',
    transaction: ''
  });

  useEffect(() => {
    if (dateRange && dateRange[0]) {
      const startDate = new Date(dateRange[0]);
      startDate.setHours(0, 0, 0, 0);

      const endDate = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
      endDate.setHours(23, 59, 59, 999);

      startDate &&
        endDate &&
        dispatch(
          fetchHistoryBalances(format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd'))
        );
    }
  }, [dateRange, dispatch]);
  const handleChangeLimit = dataKey => {
    setPage(1);
    setLimit(dataKey);
  };

  const formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return format(date, 'dd-MM-yyyy');
  };

  const handleFiltersChange = newFilters => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      userId: '',
      operationOwnerId: '',
      type: '',
      transaction: ''
    });
    setPage(1);
  };

  let filteredData = data.filter(item => {
    const fullName = `${item.user?.firstname || ''} ${item.user?.lastname || ''}`.toLowerCase();
    const registrationNumber = item.user?.registrationNumber?.toLowerCase() || '';
    const operationOwnerName = `${item.operation_owner?.firstname || ''} ${
      item.operation_owner?.lastname || ''
    }`.toLowerCase();
    const type = item.Request?.type?.toLowerCase() || '';
    const transaction = item.TransactionType?.toLowerCase() || '';
    const newLeave = item.Request?.NewLeaveBalance || '';
    const oldLeave = item.Request?.oldLeaveBalance || '';
    const nbDays = item.Request?.NB_Days || '';

    if (filters.userId && item.user?._id !== filters.userId) return false;
    if (filters.operationOwnerId && item.operation_owner?._id !== filters.operationOwnerId)
      return false;
    if (filters.type && type !== filters.type.toLowerCase()) return false;
    if (filters.transaction && transaction !== filters.transaction.toLowerCase()) return false;

    const searchTerms = searchKeyword.toLowerCase().trim().split(/\s+/);

    if (searchTerms.length === 0 || searchTerms[0] === '') return true;

    const searchableText = `${fullName} ${registrationNumber} ${operationOwnerName} ${newLeave} ${nbDays} ${oldLeave} ${type} ${transaction}`;

    return searchTerms.every(term => searchableText.includes(term));
  });
  const handleSearch = searchTerm => {
    setSearchKeyword(searchTerm);
    setPage(1);
  };
  const { t } = useTranslation();

  const start = limit * (page - 1);
  const end = start + limit;
  const paginatedData = filteredData.slice(start, end);

  const handleExport = () => {
    const dataToExport: any = [];
    filteredData.map((rowData: any) => {
      dataToExport.push({
        'First Name & Last Name': `${rowData.user?.firstname || ''} ${
          rowData.user?.lastname || ''
        }`,
        'Registration Number': rowData.user?.registrationNumber,
        Type: rowData?.Request?.type,
        Validator: rowData.idReciever
          ? `${rowData.idReciever?.firstname || ''} ${rowData.idReciever?.lastname || ''}`
          : '',
        'Days Requested': rowData.Request?.startDate
          ? `${rowData.Request?.startDate
              ?.toString()
              .substring(0, 10)
              .split('-')
              .reverse()
              .join('-')} ${rowData.Request?.startSlot} To ${rowData.Request?.endDate
              ?.toString()
              .substring(0, 10)
              .split('-')
              .reverse()
              .join('-')} ${rowData.Request?.endSlot}`
          : '',
        Transaction: rowData.TransactionType
          ? rowData.TransactionType.replace(/_/g, ' ')
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ')
          : '',
        'Old Balance': rowData.Request?.oldLeaveBalance,
        'Days Counted': rowData.Request?.NB_BalanceDays,
        'New Balance': rowData.Request?.NewLeaveBalance,
        'Operation Owner': `${rowData.operation_owner?.firstname || ''} ${
          rowData.operation_owner?.lastname || ''
        }`,
        'Created at': formatDate(rowData?.createdAt)
      });
    });

    downloadExcel(
      dataToExport,
      `LEAVE_HISTORY_${format(dateRange[0], 'dd-MM-yyyy')}_${format(dateRange[1], 'dd-MM-yyyy')}`
    );
  };

  const downloadExcel = (dataToExport, name) => {
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${name}.xlsx`);
  };

  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <InputGroup inside>
            <DateRangePicker
              appearance="default"
              placeholder={t('Select Date Range')}
              style={{ width: 230 }}
              value={dateRange}
              onChange={setDateRange}
              editable={false}
              ranges={[
                {
                  label: t('Last 30 Days'),
                  value: [new Date(new Date().setDate(new Date().getDate() - 30)), new Date()]
                },
                {
                  label: t('Last 7 Days'),
                  value: [new Date(new Date().setDate(new Date().getDate() - 7)), new Date()]
                },
                {
                  label: t('Today'),
                  value: [new Date(new Date().setDate(new Date().getDate())), new Date()]
                }
              ]}
            />
          </InputGroup>
        </Stack>

        <Stack spacing={6}>
          <InputGroup inside>
            <Input placeholder={t('Search')} value={searchKeyword} onChange={handleSearch} />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
          <Button appearance="primary" onClick={() => setShowDrawer(true)}>
            <MdFilterList style={{ marginRight: 8 }} /> {t('Filters')}
          </Button>
          <Button appearance="primary" onClick={() => handleExport()}>
            <ExportIcon style={{ marginRight: 8 }} /> {t('Export')}
          </Button>
        </Stack>
      </Stack>
      <>
        <Table
          height={Math.max(getHeight(window) - 200, 400)}
          data={paginatedData}
          loading={loading}
        >
          <Column flexGrow={4}>
            <HeaderCell>{t('First Name & Last Name')}</HeaderCell>
            <Cell>
              {rowData => `${rowData.user?.firstname || ''} ${rowData.user?.lastname || ''}`}
            </Cell>
          </Column>
          <Column flexGrow={3}>
            <HeaderCell>{t('Registration Number')}</HeaderCell>
            <Cell>{rowData => rowData.user?.registrationNumber}</Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Type')}</HeaderCell>
            <Cell>{rowData => rowData?.Request?.type || ''}</Cell>
          </Column>

          <Column flexGrow={5}>
            <HeaderCell>{t('Validator')}</HeaderCell>
            <Cell>
              {rowData =>
                rowData.idReciever
                  ? `${rowData.idReciever?.firstname || ''} ${rowData.idReciever?.lastname || ''}`
                  : ''
              }
            </Cell>
          </Column>

          <Column flexGrow={10}>
            <HeaderCell>{t('Days Requested')}</HeaderCell>
            <Cell>
              {rowData =>
                rowData.Request?.startDate
                  ? `${rowData.Request?.startDate
                      ?.toString()
                      .substring(0, 10)
                      .split('-')
                      .reverse()
                      .join('-')} ${rowData.Request?.startSlot} To ${rowData.Request?.endDate
                      ?.toString()
                      .substring(0, 10)
                      .split('-')
                      .reverse()
                      .join('-')} ${rowData.Request?.endSlot}`
                  : ''
              }
            </Cell>
          </Column>

          <Column flexGrow={5}>
            <HeaderCell>{t('Transaction')}</HeaderCell>
            <Cell>
              {rowData =>
                rowData.TransactionType
                  ? rowData.TransactionType.replace(/_/g, ' ')
                      .split(' ')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                      .join(' ')
                  : ''
              }
            </Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Old Balance')}</HeaderCell>
            <Cell>{rowData => rowData.Request?.oldLeaveBalance}</Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Days Counted')}</HeaderCell>
            <Cell>{rowData => rowData.Request?.NB_BalanceDays}</Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('New Balance')}</HeaderCell>
            <Cell>{rowData => rowData.Request?.NewLeaveBalance}</Cell>
          </Column>

          <Column flexGrow={4}>
            <HeaderCell>{t('Operation Owner')}</HeaderCell>
            <Cell>
              {rowData =>
                `${rowData.operation_owner?.firstname || ''} ${
                  rowData.operation_owner?.lastname || ''
                }`
              }
            </Cell>
          </Column>

          <Column flexGrow={4}>
            <HeaderCell>{t('Created at')}</HeaderCell>
            <Cell>{rowData => formatDate(rowData?.createdAt)}</Cell>
          </Column>
        </Table>
        <div style={{ padding: 20 }}>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={filteredData.length}
            limitOptions={[10, 30, 50]}
            limit={limit}
            activePage={page}
            onChangePage={setPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      </>

      <DrawerView
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        onFiltersChange={handleFiltersChange}
        onResetFilters={handleResetFilters}
        currentFilters={filters}
        users={data.map(item => item.user).filter(Boolean)}
        operationOwners={data.map(item => item.operation_owner).filter(Boolean)}
        usersLoading={loading}
      />
    </>
  );
};

export default DataTable;
